import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useLoaderStore } from './loader.store';
import * as transformsApi from '../api/transforms.api';
import { fetchTopics } from '../api/topics.api';
import _ from 'lodash';
import { useRouter } from 'vue-router';

const mockedTransformTypes = ['fan_out', 'typescript_map_filter', 'toast_handling', 'un_nesting', 'sql_join'];

export const useTransformsStore = defineStore('transforms', () => {
    const router = useRouter();

    const transformsForConnectors = ref(undefined);
    const transforms = ref(null);
    const totalSize = ref(0);
    const page = ref(0);
    const pageSize = ref(0);
    const lastSearchQuery = ref(null);
    const transformConfigs = ref(null);

    return {
        // State
        transforms,
        transformsForConnectors,
        totalSize,
        page,
        pageSize,
        lastSearchQuery,
        transformConfigs,
        // Getters
        total: computed(() => totalSize.value),
        all: computed(() => transforms.value),
        allForConnectors: computed(() => transformsForConnectors.value),
        configs: computed(() => transformConfigs.value),
        // Actions
        getById: async function (id, forceLoad = false) {
            if (!id) return;

            const loader = useLoaderStore();
            loader.activateLoading('transforms.fetch');
            loader.activateLoading(`transforms.fetch.${id}`);

            try {
                const data = await transformsApi.fetchTransform(id);

                if (!data) {
                    router.replace({ name: '404', params: { customMessage: 'Not found' } });
                    return;
                }

                if (transforms.value?.length) {
                    transforms.value = transforms?.value?.map((transform) => {
                        if (transform.id === id) {
                            return {
                                ...transform,
                                ...data
                            };
                        }

                        return transform;
                    });
                }

                return data;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('transforms.fetch');
                loader.deactivateLoading(`transforms.fetch.${id}`);
            }
        },
        fetch: async function ({ page_size, page_number, partial_name } = {}, forceFetch = false) {
            if (
                !forceFetch &&
                !partial_name &&
                partial_name !== '' &&
                !lastSearchQuery.value &&
                lastSearchQuery.value !== '' &&
                page_number === page.value &&
                !_.isEmpty(transforms.value) &&
                pageSize.value === page_size
            )
                return transforms.value;

            const loader = useLoaderStore();
            loader.activateLoading('transforms.fetch');

            try {
                const { result, total } = await transformsApi.fetchTransforms({
                    page_size,
                    page_number: page_number + 1,
                    partial_name
                });

                transforms.value = _.isEmpty(result || []) ? [] : result;
                page.value = page_number;
                pageSize.value = page_size;
                totalSize.value = total;
                lastSearchQuery.value = partial_name;

                return result;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('transforms.fetch');
            }
        },
        fetchTransformConfigs: async () => {
            const loader = useLoaderStore();
            loader.activateLoading('metrics.transforms.fetchTransformConfigs');
            try {
                if (transformConfigs.value && !_.isEmpty(transformConfigs.value)) {
                    return transformConfigs.value;
                }

                const data = await transformsApi.fetchTransformConfigs();
                transformConfigs.value = data;

                return data;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('metrics.transforms.fetchTransformConfigs');
            }
        },
        fetchForConnectors: async function (forceFetch = false, pageSizeNumber = 1000) {
            if (transformsForConnectors.value && !_.isEmpty(transformsForConnectors.value) && !forceFetch) {
                return transformsForConnectors.value;
            }

            const loader = useLoaderStore();
            loader.activateLoading('transforms.fetch');

            try {
                const data = await transformsApi.fetchTransformsForConnectors(pageSizeNumber);
                transformsForConnectors.value = data.result;
                return data;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('transforms.fetch');
            }
        },
        create: async function (payload) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.create');

            try {
                const data = await transformsApi.createTransform(payload);

                if (!transforms.value) {
                    transforms.value = [];
                }

                transforms.value.unshift(data);

                if (transforms.value?.length > pageSize.value) {
                    transforms.value = transforms.value.slice(0, pageSize.value);
                }

                window.analytics?.track('Transform Created', {
                    date: new Date(),
                    source: data.connector,
                    id: data.id,
                    name: data.name
                });

                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.create');
            }
        },

        clone: async function (transformId, name = null) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.clone');

            try {
                const data = await transformsApi.cloneTransform(transformId, name);

                if (!transforms.value) {
                    transforms.value = [];
                }

                transforms.value.unshift(data);

                if (transforms.value?.length > pageSize.value) {
                    transforms.value = transforms.value.slice(0, pageSize.value);
                }

                window.analytics?.track('Transform Cloned', {
                    date: new Date(),
                    source: data.transform_type,
                    id: data.id,
                    name: data.name,
                    originalId: transformId
                });

                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.clone');
            }
        },

        editTransform: async function (transform) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.edit');

            try {
                const data = await transformsApi.editTransform(transform);

                if (transforms.value) {
                    transforms.value = transforms.value.map((t) => {
                        if (t.id === data.id) {
                            return {
                                ...t,
                                ...data
                            };
                        }

                        return t;
                    });
                }

                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.edit');
            }
        },

        delete: async function (id) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.delete');
            loader.activateLoading(`transforms.delete.${id}`);

            try {
                await transformsApi.deleteTransform(id);
                transforms.value = transforms.value.filter((t) => t.id !== id);
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('transforms.delete');
                loader.deactivateLoading(`transforms.delete.${id}`);
            }
        },

        getImplementationDetails: async function (id) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.getImplementationDetails');
            loader.activateLoading(`transforms.getImplementationDetails.${id}`);

            try {
                const data = await transformsApi.transformImplementationDetails(id);
                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.getImplementationDetails');
                loader.deactivateLoading(`transforms.getImplementationDetails.${id}`);
            }
        },

        editImplementationDetails: async function (id, impl_details) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.editImplementationDetails');

            try {
                const data = await transformsApi.editImplementationDetails(id, impl_details);
                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.editImplementationDetails');
            }
        },

        topicsForPattern: async function (topicPattern, replacementPattern) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.topicsForPattern');

            try {
                const data = await transformsApi.topicsForPattern(topicPattern, replacementPattern);
                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.topicsForPattern');
            }
        },
        clearOutputTopics: async function (id, outputTopicPattern) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.clearOutputTopics');
            loader.activateLoading(`transforms.clearOutputTopics.${id}`);

            try {
                const data = await transformsApi.clearOutputTopics(id, outputTopicPattern);
                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.clearOutputTopics');
                loader.deactivateLoading(`transforms.clearOutputTopics.${id}`);
            }
        },

        runUnitTests: async function (id, versionId) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.runUnitTests');

            try {
                const data = await transformsApi.runUnitTests(id, versionId);
                return data;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('transforms.runUnitTests');
            }
        },
        deployPreview: async function (id, versionId, replayWindow) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.deployPreview');

            try {
                const data = await transformsApi.deployPreview(id, versionId, replayWindow);
                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.deployPreview');
            }
        },
        deployLive: async function (id, versionId) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.deployLive');

            try {
                const data = await transformsApi.deployLive(id, versionId);
                return data;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('transforms.deployLive');
            }
        },
        listTopicsForTransform: async function (id) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.listTopicsForTransform');

            try {
                const data = await fetchTopics({ partial_name: `transform_${id}` });
                return data;
            } catch (err) {
                console.error(err);
            } finally {
                loader.deactivateLoading('transforms.listTopicsForTransform');
            }
        },
        getJobStatus: /** @returns {Promise<TransformJobStatusRes>}*/ async function (id, showToaster = true) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.getJobStatus');
            loader.activateLoading(`transforms.getJobStatus.${id}`);

            try {
                const data = await transformsApi.getJobStatus(id, showToaster);
                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.getJobStatus');
                loader.deactivateLoading(`transforms.getJobStatus.${id}`);
            }
        },
        getOutputTopicsForTransform: /** @returns {Promise<TransformJobStatusRes>}*/ async function (id, showToaster = true) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.getOutputTopicsForTransform');
            loader.activateLoading(`transforms.getOutputTopicsForTransform.${id}`);

            try {
                const data = await transformsApi.getOutputTopicsForTransform(id, showToaster);
                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.getOutputTopicsForTransform');
                loader.deactivateLoading(`transforms.getOutputTopicsForTransform.${id}`);
            }
        },
        setInputOutputTopic: async function (id, topicPattern, replacementPattern) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.setInputOutputTopic');
            loader.activateLoading(`transforms.setInputOutputTopic.${id}`);

            try {
                const data = await transformsApi.setInputOutputTopic(id, topicPattern, replacementPattern);
                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.setInputOutputTopic');
                loader.deactivateLoading(`transforms.setInputOutputTopic.${id}`);
            }
        },
        getErrorsForTransform: /** @returns {Promise<TransformJobError[]>}*/ async function (id, showToaster = true) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.getErrorsForTransform');
            loader.activateLoading(`transforms.getErrorsForTransform.${id}`);

            try {
                const data = await transformsApi.getErrorsForTransform(id, showToaster);
                return data;
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                loader.deactivateLoading('transforms.getErrorsForTransform');
                loader.deactivateLoading(`transforms.getErrorsForTransform.${id}`);
            }
        },
        getDlqErrorsForTransform: /** @returns {Promise<DlqError|null>}*/ async function (id, showToaster = true) {
            const loader = useLoaderStore();
            loader.activateLoading('transforms.getDlqErrorsForTransform');
            loader.activateLoading(`transforms.getDlqErrorsForTransform.${id}`);

            try {
                const data = await transformsApi.getDlqErrorsForTransform(id, showToaster);
                return data;
            } catch (err) {
                throw err;
            } finally {
                loader.deactivateLoading('transforms.getDlqErrorsForTransform');
                loader.deactivateLoading(`transforms.getDlqErrorsForTransform.${id}`);
            }
        }
    };
});
